import React, { useState } from "react";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/perms";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const ACTIONS = [
  ["assign_officer", "Assign admissions officer", "reassign"],
  ["assign_bdm", "Reassign BDM", "reassign"],
  ["set_intake", "Set intake", "edit"],
  ["set_category", "Set program category", "edit"],
];

export default function BulkActionsBar({ selected, filters, total, meta, perms, onDone }) {
  const [action, setAction] = useState("");
  const [value, setValue] = useState("");
  const [mode, setMode] = useState("selected");
  const [confirm, setConfirm] = useState(null);
  const users = meta.users || [];
  const options = {
    assign_officer: users.filter(u => u.role === "admissions_officer").map(u => [u.user_id, u.name]),
    assign_bdm: users.filter(u => u.role === "bdm").map(u => [u.user_id, u.name]),
    set_intake: (meta.intakes || []).map(i => [i.id, i.name]),
    set_category: CATEGORIES.map(c => [c, c]),
  }[action] || [];
  const body = () => ({ action, value, mode, ids: selected, filters: Object.fromEntries(Object.entries(filters).filter(([_, v]) => v && v !== "all")) });

  const preview = async () => {
    if (!action || !value) return toast.error("Choose an action and a value");
    if (mode === "selected" && !selected.length) return toast.error("Tick at least one row, or choose 'All filtered records'");
    try { const r = await api.post("/applications/bulk", body()); setConfirm(r.data.count); }
    catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };
  const apply = async () => {
    try {
      const r = await api.post("/applications/bulk", { ...body(), confirm: true });
      toast.success(`${r.data.changed} application(s) updated`); setConfirm(null); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk action failed"); }
  };
  const label = ACTIONS.find(a => a[0] === action)?.[1];
  const valLabel = options.find(o => o[0] === value)?.[1];

  return (
    <div data-testid="bulk-bar" className="flex flex-wrap items-center gap-2 bg-orange-50/60 border border-orange-200 rounded-xl p-3 text-sm">
      <span className="text-xs uppercase tracking-wide text-orange-800 font-semibold">Bulk</span>
      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger data-testid="bulk-mode" className="h-9 w-[230px] bg-white"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="selected">Selected rows ({selected.length})</SelectItem>
          <SelectItem value="filtered">All filtered records ({total})</SelectItem>
        </SelectContent>
      </Select>
      <Select value={action} onValueChange={v => { setAction(v); setValue(""); }}>
        <SelectTrigger data-testid="bulk-action" className="h-9 w-[220px] bg-white"><SelectValue placeholder="Choose action" /></SelectTrigger>
        <SelectContent>{ACTIONS.filter(a => perms[a[2]]).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={value} onValueChange={setValue} disabled={!action}>
        <SelectTrigger data-testid="bulk-value" className="h-9 w-[220px] bg-white"><SelectValue placeholder="Choose value" /></SelectTrigger>
        <SelectContent>{options.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Button data-testid="bulk-preview-btn" size="sm" onClick={preview} className="bg-orange-500 hover:bg-orange-600 text-white">Review</Button>
      <Dialog open={confirm !== null} onOpenChange={o => !o && setConfirm(null)}>
        <DialogContent data-testid="bulk-confirm-dialog" className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-font">Confirm bulk change</DialogTitle>
            <DialogDescription>{label} → <b>{valLabel}</b></DialogDescription>
          </DialogHeader>
          <p data-testid="bulk-confirm-count" className="text-sm text-slate-700"><b>{confirm}</b> application(s) {mode === "filtered" ? "matching the current filters" : "selected"} will be checked. Ones that already have this value are left alone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button data-testid="bulk-confirm-btn" onClick={apply} className="bg-orange-500 hover:bg-orange-600 text-white">Apply to {confirm}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
